import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type Control, useController } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { getUserEducation } from '~/actions';
import { Column, Row, SmartTooltip, Text } from '~/commonComponents';
import { UserProfession } from '~/enums/UserProfession';
import { useAppSelector } from '~/hooks/useAppSelector';
import InfoIcon from '~/images/icons/Info';
import PlusIcon from '~/images/icons/PlusIcon';
import { EducationUtils } from '~/utils/EducationUtils';
import { type IShiftDetailsFormValues } from '../../useShiftDetailsForm';
import { ExpectedProceduresPopup } from './ExpectedProceduresPopup';
import { ProcedurePill } from './ProcedurePill';

interface IExpectedProceduresFieldProps {
  control: Control<IShiftDetailsFormValues>;
  professionType: string;
  preferredSpecialty: string;
}

export const ExpectedProceduresField = ({
  control,
  professionType,
  preferredSpecialty,
}: IExpectedProceduresFieldProps) => {
  const { field } = useController({ name: 'procedures', control });
  const dispatch = useDispatch();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const isInitialMount = useRef(true);

  const userEducation = useAppSelector((state) => state.user.userEducation);

  useEffect(() => {
    if (!professionType) return;
    const code = UserProfession.getCode(professionType) as string | undefined;
    if (!code) return;
    const professionCode = UserProfession.getNormalizedCode(code);
    dispatch(getUserEducation({ profession: professionCode }));
  }, [dispatch, professionType]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    field.onChange([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionType, preferredSpecialty]);

  const professionCode = professionType
    ? (UserProfession.getCode(professionType) as string | undefined)
    : undefined;
  const normalizedCode = professionCode
    ? (UserProfession.getNormalizedCode(professionCode) as string)
    : '';
  const specialtiesAndProcedures = (userEducation as any)?.[normalizedCode]
    ?.specialtiesAndProcedures;

  const procedures = useMemo(() => {
    if (!normalizedCode || !specialtiesAndProcedures) return [];
    return (
      EducationUtils.getProceduresOfProfession({
        specialtiesAndProcedures,
        profession: normalizedCode,
        specialties: preferredSpecialty ? [preferredSpecialty] : [],
      }) ?? []
    );
  }, [normalizedCode, specialtiesAndProcedures, preferredSpecialty]);

  const isAddDisabled = !procedures.length || field.value.length >= 4;

  const handleRemoveProcedure = (procedure: string) => {
    field.onChange(field.value.filter((p: string) => p !== procedure));
  };

  const handleConfirm = (selected: string[]) => {
    field.onChange(selected);
    setIsPopupOpen(false);
  };

  return (
    <>
      <Column data-testid="expected-procedures-field" data-cy="expected-procedures-field" gap={8}>
        <Row justifyContent="space-between" alignItems="center">
          <Row alignItems="center" gap={6}>
            <Text bold fontSize={18} color="neutral_600">
              Expected Procedures:
            </Text>
            <SmartTooltip
              position="bottom"
              content={
                <Text color="white" bold>
                  Procedures:
                  <br />
                  <br />
                  Here you can select what the expected procedures will be for the professional.
                  This will provide clarity for visiting professionals, improve matching accuracy,
                  and elevate post-shift ratings.
                </Text>
              }
            >
              <InfoIcon color={colors.neutral[300]} />
            </SmartTooltip>
          </Row>
          <Row
            alignItems="center"
            gap={4}
            onClick={isAddDisabled ? undefined : () => setIsPopupOpen(true)}
            style={{
              color: isAddDisabled ? colors.secondary[200] : colors.secondary[500],
              cursor: isAddDisabled ? 'default' : 'pointer',
            }}
            data-testid="add-procedures-button"
            data-cy="add-procedures-button"
          >
            <PlusIcon color={isAddDisabled ? colors.secondary[200] : colors.secondary[500]} />
            <Text
              bold
              fontSize={18}
              color={isAddDisabled ? colors.secondary[200] : colors.secondary[500]}
            >
              Add Procedures
            </Text>
          </Row>
        </Row>

        {field.value.length > 0 && (
          <Row
            style={{ flexWrap: 'wrap', gap: 8, marginTop: 16, marginBottom: 8 }}
            data-testid="procedures-pills-container"
            data-cy="procedures-pills-container"
          >
            {field.value.map((procedure: string) => (
              <ProcedurePill
                key={procedure}
                label={procedure.split('_')[1]}
                onRemove={() => handleRemoveProcedure(procedure)}
              />
            ))}
          </Row>
        )}

        {procedures.length === 0 && field.value.length === 0 && professionType && (
          <Typography
            italic
            color={colors.neutral[500]}
            data-testid="no-procedures-message"
            data-cy="no-procedures-message"
            style={{ fontSize: 14, marginTop: 8 }}
          >
            There are no procedures for this specialty preference at this time.
          </Typography>
        )}
      </Column>

      {isPopupOpen && (
        <ExpectedProceduresPopup
          procedures={procedures}
          selected={field.value}
          onConfirm={handleConfirm}
          onClose={() => setIsPopupOpen(false)}
        />
      )}
    </>
  );
};
